import { createReducer, on } from '@ngrx/store';
import {
  BudgetVsCommitGeneralCashflow,
  BudgetVsCommitProjectCashflow,
  BudgetVsPrimeGeneralCashflow,
  BudgetVsPrimeProjectCashflow,
  CASHFLOW_STATUS,
  CASHFLOW_TYPES,
  CashflowFilters,
  PrimeVsCommitGeneralCashflow,
  PrimeVsCommitProjectCashflow,
} from '../../framework/cash-flow/cashflow-table/cashflow.types';
import { cashflowActions } from './cashflow.actions';

export const cashflowFeatureKey = 'cashflow';

export interface CashflowState {
  status: CASHFLOW_STATUS;

  filters: CashflowFilters;

  cashflowType: CASHFLOW_TYPES;

  cashflow: {
    [CASHFLOW_TYPES.budgetVsCommitGeneral]: BudgetVsCommitGeneralCashflow;
    [CASHFLOW_TYPES.budgetVsCommitsByProject]: Map<number, BudgetVsCommitProjectCashflow>;
    [CASHFLOW_TYPES.budgetVsPrimeGeneral]: BudgetVsPrimeGeneralCashflow;
    [CASHFLOW_TYPES.budgetVsPrimesByProject]: Map<number, BudgetVsPrimeProjectCashflow>;
    [CASHFLOW_TYPES.primeVsCommitGeneral]: PrimeVsCommitGeneralCashflow;
    [CASHFLOW_TYPES.primeVsCommitsByProject]: Map<number, PrimeVsCommitProjectCashflow>;
  };
}

export const initialState: CashflowState = {
  status: CASHFLOW_STATUS.IDLE,
  cashflowType: CASHFLOW_TYPES.budgetVsCommitGeneral,
  filters: {
    projectIds: [],
    propertyIds: [],
    year: new Date().getFullYear(),
  },
  cashflow: {
    [CASHFLOW_TYPES.budgetVsCommitGeneral]: null,
    [CASHFLOW_TYPES.budgetVsCommitsByProject]: new Map(),
    [CASHFLOW_TYPES.budgetVsPrimeGeneral]: null,
    [CASHFLOW_TYPES.budgetVsPrimesByProject]: new Map(),
    [CASHFLOW_TYPES.primeVsCommitGeneral]: null,
    [CASHFLOW_TYPES.primeVsCommitsByProject]: new Map(),
  },
};

export const cashflowReducer = createReducer(
  initialState,
  on(cashflowActions.filtersChanged, (state, action): CashflowState => {
    return {
      ...state,
      filters: {
        ...state.filters,
        ...action.filters,
      },
      cashflowType: action.cashflowType ? action.cashflowType : state.cashflowType,
    };
  }),

  on(cashflowActions.budgetVsCommitGeneralLoadedSuccess, (state, action): CashflowState => {
    return {
      ...state,
      cashflow: {
        ...state.cashflow,
        budgetVsCommitGeneral: action.data,
      },
      status: CASHFLOW_STATUS.LOADED,
    };
  }),

  on(cashflowActions.budgetVsCommitByProjectLoadedSuccess, (state, action): CashflowState => {
    const budgetVsCommitsByProject = state.cashflow.budgetVsCommitsByProject;
    budgetVsCommitsByProject.set(action.projectId, action.data);
    return {
      ...state,
      cashflow: {
        ...state.cashflow,
        budgetVsCommitsByProject,
      },
      status: CASHFLOW_STATUS.LOADED,
    };
  }),

  on(cashflowActions.budgetVsPrimeGeneralLoadedSuccess, (state, action): CashflowState => {
    return {
      ...state,
      cashflow: {
        ...state.cashflow,
        budgetVsPrimeGeneral: action.data,
      },
      status: CASHFLOW_STATUS.LOADED,
    };
  }),

  on(cashflowActions.budgetVsPrimeByProjectLoadedSuccess, (state, action): CashflowState => {
    const budgetVsPrimesByProject = state.cashflow.budgetVsPrimesByProject;
    budgetVsPrimesByProject.set(action.projectId, action.data);
    return {
      ...state,
      cashflow: {
        ...state.cashflow,
        budgetVsPrimesByProject,
      },
      status: CASHFLOW_STATUS.LOADED,
    };
  }),

  on(cashflowActions.primeVsCommitGeneralLoadedSuccess, (state, action): CashflowState => {
    return {
      ...state,
      cashflow: {
        ...state.cashflow,
        primeVsCommitGeneral: action.data,
      },
      status: CASHFLOW_STATUS.LOADED,
    };
  }),

  on(cashflowActions.primeVsCommitByProjectLoadedSuccess, (state, action): CashflowState => {
    const primeVsCommitsByProject = state.cashflow.primeVsCommitsByProject;
    primeVsCommitsByProject.set(action.projectId, action.data);
    return {
      ...state,
      cashflow: {
        ...state.cashflow,
        primeVsCommitsByProject,
      },
      status: CASHFLOW_STATUS.LOADED,
    };
  }),

  on(
    cashflowActions.loadBudgetVsCommitGeneral,
    cashflowActions.loadBudgetVsCommitByProject,
    cashflowActions.loadBudgetVsPrimeGeneral,
    cashflowActions.loadBudgetVsPrimeByProject,
    cashflowActions.loadPrimeVsCommitGeneral,
    cashflowActions.loadPrimeVsCommitByProject,
    (state): CashflowState => {
      const isEmpty =
        state.cashflow[state.cashflowType] === null ||
        (state.cashflow[state.cashflowType] as Map<unknown, unknown>)?.size === 0;
      return {
        ...state,
        status: isEmpty ? CASHFLOW_STATUS.LOADING : CASHFLOW_STATUS.RELOADING,
      };
    },
  ),

  on(
    cashflowActions.budgetVsCommitGeneralLoadedError,
    cashflowActions.budgetVsCommitByProjectLoadedError,
    cashflowActions.budgetVsPrimeGeneralLoadedError,
    cashflowActions.budgetVsPrimeByProjectLoadedError,
    cashflowActions.primeVsCommitGeneralLoadedError,
    cashflowActions.primeVsCommitByProjectLoadedError,
    (state): CashflowState => {
      return {
        ...state,
        status: CASHFLOW_STATUS.ERROR,
      };
    },
  ),
);
